import { useState, useEffect } from "react";
import PageLayout from "components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import SettingsCard from "components/commons/SettingsCard";
import SuccessMessage from "components/commons/SuccessMessage";
import { useGetAiSettingsQuery, useSetAiSettingsMutation } from "./aiSettingsApiSlice";

export default function AiSettings() {
    const [t] = useTranslation(["global", "ai"]);

    const [settings, setSettings] = useState();
    const [statusMessage, setStatusMessage] = useState("");

    const {
        data,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch
    } = useGetAiSettingsQuery();

    const [saveSettings, {
        isLoading: saveIsLoading,
        isSuccess: saveIsSuccess,
        isError: saveIsError,
        error: saveError,
        reset: resetSaveStatus
    }] = useSetAiSettingsMutation();

    useEffect(() => {
        if (data && isSuccess) {
            setSettings(data);
        }
    }, [data, isSuccess]);

    const handleChange = (key, value) => {
        setSettings((prevSettings) => ({
            ...prevSettings,
            [key]: value,
        }));
        resetSaveStatus();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        saveSettings(settings);
    };

    const content = (
        <div>
            {isError ? (
                <p>{t("Failed to load settings.")} {error?.data?.message || error?.status}</p>
            ) : isSuccess && settings ? (
                <form onSubmit={handleSubmit}>
                    {saveIsSuccess && <SuccessMessage />}
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 mb-4">
                        <div className="col">
                            <SettingsCard
                                label={t("ai:setting.model.title")}
                                description={t("ai:setting.model.desc")}
                                logo={<i className="fa-solid fa-brain"></i>}
                                value={settings.model}
                                onChange={(e) => handleChange("model", e.target.value)}
                                inputType="text"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("ai:setting.temperature.title")}
                                description={t("ai:setting.temperature.desc")}
                                logo={<i className="fa-solid fa-temperature-empty"></i>}
                                value={settings.temp}
                                onChange={(e) => handleChange("temp", parseFloat(e.target.value))}
                                inputType="range"
                                step={0.1}
                                rangeMin={0}
                                rangeMax={0.9}
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("ai:setting.store.title")}
                                description={t("ai:setting.store.desc")}
                                logo={<i className="fa-solid fa-box-archive"></i>}
                                value={settings.store}
                                onChange={(e) => handleChange("store", e.target.checked)}
                                inputType="checkbox"
                            />
                        </div>
                    </div>
                    <div className="row mb-4">
                        <div className="col">
                            <SettingsCard
                                label={t("ai:setting.apiKey.title")}
                                description={t("ai:setting.apiKey.desc")}
                                logo={<i className="fa-solid fa-key"></i>}
                                value={settings.apiKey}
                                onChange={(e) => handleChange("apiKey", e.target.value)}
                                inputType="text-area"
                            />
                        </div>
                    </div>
                    <div className="row mb-4">
                        <div className="col">
                            <SettingsCard
                                label={t("ai:setting.welcome.title")}
                                description={t("ai:setting.welcome.desc")}
                                logo={<i className="fa-regular fa-comment-dots"></i>}
                                value={settings.welcome}
                                onChange={(e) => handleChange("welcome", e.target.value)}
                                inputType="text-area"
                                rows={8}
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn btn-primary"><i className="fa-solid fa-floppy-disk"></i> {t("action.save")}</button>


                </form>
            ) : <></>}
        </div>
    );

    return (
        <PageLayout
            pageTitle={t("Ai Settings")}
            bodyContent={content}
            onPageRefresh={refetch}
            isLoadingContent={saveIsLoading || isFetching}
            isLoadingPage={isLoading}
            isError={saveIsError || isError}
            errorMsg={saveError || error}
        />
    );
}
